import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  formatCount,
  formatWordCount,
  getNovelById,
  getRelatedNovels,
} from "@/lib/novel";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/base/Breadcrumb";
import NovelCard from "@/components/base/NovelCard";
import ChapterList from "@/pages/detail/components/ChapterList";
import SharePanel from "@/pages/detail/components/SharePanel";

export default function DetailPage() {
  const { id = "" } = useParams();
  const novel = getNovelById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [introExpanded, setIntroExpanded] = useState(false);
  const [chaptersOpen, setChaptersOpen] = useState(false);

  useDocumentMeta(
    novel ? `${novel.title}在线阅读 - 御书屋` : "小说未找到 - 御书屋",
    novel
      ? `${novel.intro} 作者：${novel.author}，分类：${novel.categoryName}，共 ${novel.chapters.length} 章，支持御书屋在线阅读。`
      : "抱歉，你访问的小说不存在或已下架。",
  );

  useEffect(() => {
    setIntroExpanded(false);
    setChaptersOpen(false);
  }, [id]);

  if (!novel) {
    return (
      <div className="w-full px-4 py-20 md:px-8">
        <div className="mx-auto flex max-w-xl flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
            <i className="ri-error-warning-line"></i>
          </span>
          <h1 className="font-heading text-xl font-semibold text-foreground-800">小说未找到</h1>
          <p className="mt-2 text-sm text-foreground-500">
            你访问的小说不存在或已经下架，不妨回首页看看其他好书。
          </p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const favorite = isFavorite(novel.id);
  const firstChapter = novel.chapters[0];
  const related = getRelatedNovels(novel, 6);
  const shareUrl = `${window.location.origin}${__BASE_PATH__}detail/${novel.id}`;

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <div className="mb-5">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: `${novel.categoryName}小说`, to: `/category/${novel.category}` },
            { label: novel.title },
          ]}
        />
      </div>

      <section className="mb-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
        <div className="flex flex-col gap-6 md:flex-row">
          <div className="mx-auto h-64 w-44 shrink-0 overflow-hidden rounded-lg bg-background-200 md:mx-0 md:h-72 md:w-52">
            <img
              src={novel.cover}
              alt={`${novel.title} 小说封面`}
              title={`${novel.title} 在线阅读 - 御书屋`}
              className="h-full w-full object-top"
            />
          </div>

          <div className="flex flex-1 flex-col">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {novel.title}
            </h1>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <div>
                <p className="text-foreground-400">作者</p>
                <p className="mt-0.5 font-medium text-foreground-900">{novel.author}</p>
              </div>
              <div>
                <p className="text-foreground-400">分类</p>
                <Link
                  to={`/category/${novel.category}`}
                  className="mt-0.5 inline-block font-medium text-primary-600 hover:underline"
                >
                  {novel.categoryName}
                </Link>
              </div>
              <div>
                <p className="text-foreground-400">状态</p>
                <p className="mt-0.5 font-medium text-foreground-900">{novel.status}</p>
              </div>
              <div>
                <p className="text-foreground-400">字数</p>
                <p className="mt-0.5 font-medium text-foreground-900">
                  {formatWordCount(novel.wordCount)}
                </p>
              </div>
              <div>
                <p className="text-foreground-400">最新章节</p>
                <Link
                  to={`/read/${novel.id}/${novel.chapters[novel.chapters.length - 1]?.chapterId}`}
                  className="mt-0.5 line-clamp-1 inline-block font-medium text-primary-600 hover:underline"
                >
                  {novel.chapters[novel.chapters.length - 1]?.title}
                </Link>
              </div>
              <div>
                <p className="text-foreground-400">更新时间</p>
                <p className="mt-0.5 font-medium text-foreground-900">{novel.updatedAt}</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {novel.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={firstChapter ? `/read/${novel.id}/${firstChapter.chapterId}` : "#chapters"}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-book-open-line"></i>
                开始阅读
              </Link>
              <button
                type="button"
                onClick={() => toggleFavorite(novel.id)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-medium transition-colors ${
                  favorite
                    ? "border-accent-300 bg-accent-100 text-accent-700"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                {favorite ? "已收藏" : "收藏"}
              </button>
              <SharePanel title={novel.title} url={shareUrl} />
            </div>

            <p className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span>
                <i className="ri-fire-line mr-1 text-accent-500"></i>
                {formatCount(novel.clicks)} 次阅读
              </span>
              <span>
                <i className="ri-bookmark-line mr-1 text-accent-500"></i>
                {formatCount(novel.favorites)} 收藏
              </span>
            </p>
          </div>
        </div>
      </section>

      <section className="mb-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
        <h2 className="mb-3 font-heading text-xl font-bold text-foreground-950">作品简介</h2>
        <p
          className={`text-sm leading-7 text-foreground-700 ${
            introExpanded ? "" : "line-clamp-3"
          }`}
        >
          {novel.intro}
        </p>
        <button
          type="button"
          onClick={() => setIntroExpanded((prev) => !prev)}
          className="mt-2 flex items-center gap-1 text-sm font-medium text-accent-600 transition-colors hover:text-accent-700"
        >
          {introExpanded ? "收起" : "展开全文"}
          <i className={introExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </button>
      </section>

      <section id="chapters" className="mb-8 scroll-mt-20 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">章节目录</h2>
            <p className="mt-1 text-sm text-foreground-500">
              共 {novel.chapters.length} 章 · 点击章节即可开始阅读
            </p>
          </div>
          <button
            type="button"
            onClick={() => setChaptersOpen((prev) => !prev)}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            {chaptersOpen ? "收起目录" : "展开全部章节"}
            <i className={chaptersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>
        </div>

        <ChapterList
          novel={chaptersOpen ? novel : { ...novel, chapters: novel.chapters.slice(0, 8) }}
        />

        {!chaptersOpen && novel.chapters.length > 8 && (
          <button
            type="button"
            onClick={() => setChaptersOpen(true)}
            className="mt-3 w-full rounded-md border border-dashed border-background-300 py-2.5 text-sm text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            还有 {novel.chapters.length - 8} 章，点击展开全部
          </button>
        )}
      </section>

      <section>
        <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {related.map((item) => (
            <NovelCard key={item.id} novel={item} />
          ))}
        </div>
      </section>
    </div>
  );
}